import { useEffect, useRef, useState } from "react"
import { captureMessage } from "@sentry/react"

import MobileWarning from "app/components/upgrade_plan_modal/mobile_warning/index"
import {
  CREDITS_CHECKOUT_PLAN_KEYS,
  CreditsTier,
  isPaidTier,
  nextCreditsTier,
  tierDisplayName,
} from "shared/utils/tiers"

import { CreditsCheckoutData, loadCreditsCheckout } from "./api"
import CreditsCheckoutModal from "./checkout_modal"
import CreditsPlanChangeModal from "./plan_change_modal"
import { CreditsUpgradeTracking, trackCreditsUpgradeShown } from "./tracking"

interface Props {
  onClose: () => void
  onUpgraded?: (tier: CreditsTier) => void
  track?: CreditsUpgradeTracking
  user: {
    subscriptionPaymentType: string
    tier: string
  }
  visible: boolean
}

// Destination of every "Upgrade for more credits" CTA: takes a credits user one plan up.
// Free users go through checkout; paying Stripe users switch their subscription in place.
const CreditsUpgradeModal = ({ onClose, onUpgraded, track, user, visible }: Props) => {
  const targetTier = nextCreditsTier(user.tier)
  const upgradingSubscription = isPaidTier(user.tier)
  const canChangePlanOnWeb = !upgradingSubscription || user.subscriptionPaymentType === "stripe"
  const [checkout, setCheckout] = useState<CreditsCheckoutData | null>(null)
  const trackedOpen = useRef(false)

  // One view per opening, as the tiered upgrade modal does.
  useEffect(() => {
    // The reset runs even without track: /create clears track in the same render that closes the modal.
    if (!visible) {
      trackedOpen.current = false
      return
    }
    if (trackedOpen.current || !track || !targetTier) return

    trackedOpen.current = true
    trackCreditsUpgradeShown(track, user.tier)
  }, [visible])

  // Loaded on open rather than on mount: the endpoint sets up a Stripe customer and SetupIntent.
  const shouldLoad = visible && canChangePlanOnWeb && targetTier !== null && checkout?.plan.tier !== targetTier

  useEffect(() => {
    if (!shouldLoad || !targetTier) return

    const planKey = CREDITS_CHECKOUT_PLAN_KEYS[targetTier]
    let cancelled = false

    loadCreditsCheckout(planKey)
      .then(data => {
        if (!cancelled) setCheckout(data)
      })
      .catch(error => {
        if (cancelled) return

        captureMessage("Credits upgrade modal failed to load checkout data.", {
          extra: {
            targetTier,
            errorMessage: error instanceof Error ? error.message : String(error),
          },
        })
        if (upgradingSubscription) {
          window.appendFlash("Something went wrong. Please try again.", "danger")
          onClose()
        } else {
          window.location.assign(`/subscriptions/${planKey}`)
        }
      })

    return () => {
      cancelled = true
    }
  }, [shouldLoad, targetTier])

  if (!targetTier) return null
  if (!canChangePlanOnWeb) return <MobileWarning onClose={onClose} visible={visible} />
  if (!visible || checkout?.plan.tier !== targetTier) return null

  const handleUpgraded = () => onUpgraded?.(targetTier)

  if (upgradingSubscription) {
    const planName = tierDisplayName(targetTier)
    const currencyPlans = checkout.plan.currency_plans
    const yearlyPlan = (currencyPlans[checkout.default_currency] ?? Object.values(currencyPlans)[0])?.yearly_plan

    return (
      <CreditsPlanChangeModal
        productId={checkout.plan.product_id}
        planName={planName}
        cta={`Upgrade to ${planName}`}
        priceLine={yearlyPlan && `${yearlyPlan.local_price_formatted} billed annually`}
        onClose={onClose}
        onSwitched={() => {
          window.appendFlash(`Upgrade complete. Welcome to IFTTT ${planName}!`, "success")
          handleUpgraded()
          onClose()
        }}
      />
    )
  }

  return <CreditsCheckoutModal checkout={checkout} onClose={onClose} onUpgraded={handleUpgraded} visible={visible} />
}

export default CreditsUpgradeModal
